import type React from 'react';
import type { DrawingEvidence } from '../../../types/drbfm';

export interface DocumentImageSummary {
  id: string;
  pageNumber: number;
  imageType: string;
}

const IMAGE_TYPES_BY_PRIORITY = ['full_page', 'blueprint'];

const clamp = (value: number, min: number, max: number): number => (
  Math.max(min, Math.min(max, value))
);

const toPercent = (value: number): string => `${Number((value * 100).toFixed(1))}%`;

export const buildDocumentUrl = (evidence: DrawingEvidence): string => {
  const base = `/documents/${encodeURIComponent(evidence.documentId)}`;
  return evidence.pageNumber ? `${base}?page=${evidence.pageNumber}` : base;
};

export const selectPageImage = (
  images: DocumentImageSummary[],
  pageNumber?: number,
): DocumentImageSummary | undefined => {
  const pageImages = pageNumber
    ? images.filter((image) => image.pageNumber === pageNumber)
    : images;
  return pageImages.find((image) => IMAGE_TYPES_BY_PRIORITY.includes(image.imageType))
    || pageImages[0];
};

/** Inline because every value is data-derived; no utility class can express it. */
export const getHighlightStyle = (evidence: DrawingEvidence): React.CSSProperties | null => {
  if (evidence.bbox.unit !== 'normalized') {
    return null;
  }

  const left = clamp(evidence.bbox.x, 0, 1);
  const top = clamp(evidence.bbox.y, 0, 1);
  const width = clamp(evidence.bbox.width, 0, 1 - left);
  const height = clamp(evidence.bbox.height, 0, 1 - top);

  if (width <= 0 || height <= 0) {
    return null;
  }

  return {
    position: 'absolute',
    left: toPercent(left),
    top: toPercent(top),
    width: toPercent(width),
    height: toPercent(height),
    border: '3px solid #d32f2f',
    boxShadow: '0 0 0 2px rgba(211, 47, 47, 0.2)',
    pointerEvents: 'none',
  };
};
